import React, { FC, Dispatch, SetStateAction, ReactElement, useState } from 'react'
import { Formik } from 'formik'
import { Form, Alert, Image } from 'react-bootstrap'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import * as yup from 'yup'
import Auth from '@aws-amplify/auth'
import LoadingButton from '../LoadingButton'
import mavLogo from '../../assets/img/gray_logo.svg'

interface Props extends RouteComponentProps {
  setEmailUser: (emailUser: string) => void
  setShowLoginForm: Dispatch<SetStateAction<boolean>>
  setIsAuthenticated: (isAuthenticated: boolean) => void
  setNeedsConfirming: (needsConfirming: boolean) => void
  setEmail: (email: string) => void
  setPassword: (password: string) => void
}

const schema = yup.object({
  email: yup
    .string()
    .email('Email must be a valid email')
    .required('Email is a required field'),
  password: yup
    .string()
    .min(8, 'Password must be at least 8 characters')
    .required('Password is a required field'),
})

const LoginForm: FC<Props> = ({
  // setShowLoginForm,
  setIsAuthenticated,
  setNeedsConfirming,
  setEmail,
  setEmailUser,
  setPassword,
  history,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  // eslint-disable-next-line @typescript-eslint/member-delimiter-style
  const signIn = async (values: { email: string; password: string }): Promise<void> => {
    setIsLoading(true)
    try {
      const user = await Auth.signIn(values.email.toLocaleLowerCase(), values.password)

      setEmailUser(user.attributes.email)

      // On successful sign in, remove loginStage from local storage for cleanup
      localStorage.removeItem('loginStage')
      setIsAuthenticated(true)
      setErrorMessage('')
      setIsLoading(false)

      history.push('/')
    } catch (e) {
      if (e.code === 'UserNotConfirmedException') {
        setEmail(values.email)
        setPassword(values.password)
        setNeedsConfirming(true)
      } else {
        setIsAuthenticated(false)
        setErrorMessage(e.message)
        setIsLoading(false)
      }
    }
  }

  return (
    <>
      {errorMessage && (
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          <hr />
          <span>{errorMessage}</span>
        </Alert>
      )}
      <Formik
        validationSchema={schema}
        onSubmit={signIn}
        initialValues={{
          email: '',
          password: '',
        }}
      >
        {({ handleSubmit, values, handleChange, touched, errors, submitCount }): ReactElement => (
          <Form noValidate onSubmit={handleSubmit}>
            <Image src={mavLogo} alt="Placeholder" className="logo-img" />
            <div className="controls">
              <Form.Group controlId="email">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  required
                  value={values.email}
                  onChange={handleChange}
                  isValid={touched.email && !errors.email}
                  isInvalid={submitCount > 0 && !!errors.email}
                  placeholder="callsign@domainname.com"
                />
                <Form.Control.Feedback type="invalid">
                  <Alert variant="danger">{errors.email}</Alert>
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId="password">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  required
                  value={values.password}
                  onChange={handleChange}
                  isValid={touched.password && !errors.password}
                  isInvalid={submitCount > 0 && !!errors.password}
                  placeholder="*****************"
                />
                <Form.Control.Feedback type="invalid">
                  <Alert variant="danger">{errors.password}</Alert>
                </Form.Control.Feedback>
              </Form.Group>
            </div>
            <Form.Group controlId="actions" className="actions">
              <LoadingButton
                variant="primary"
                type="submit"
                block
                className="login-button"
                isLoading={isLoading}
              >
                Open my hangar
              </LoadingButton>
              <LoadingButton
                variant="primary"
                type="button"
                block
                className="login-button"
                isLoading={isLoading}
                onClick={(): void => history.push('/register')}
              >
                Sign up for success
              </LoadingButton>
              <button
                className="link_btn"
                onClick={(): void => history.push('/forgot-password')}
                onKeyDown={(): void => {}}
                type="button"
              >
                Forgot Password
              </button>
            </Form.Group>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default withRouter(LoginForm)
